import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Star, Search, MapPin, Video, Home, Building2, Circle,
  Globe, Moon, Sun, Sparkles, ArrowRight, Filter, X,
  GraduationCap, Languages, BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import TrustScoreBadge from '../components/TrustScoreBadge';
import SpecialNeedsBadge from '../components/teachers/SpecialNeedsBadge';
import SuiteSwitcher from '../components/SuiteSwitcher';
import { RATINGS_ENABLED } from '../config/featureFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { key: 'الرياضيات',           ar: 'الرياضيات',     en: 'Math',         emoji: '➗' },
  { key: 'الفيزياء',             ar: 'الفيزياء',       en: 'Physics',      emoji: '⚛️' },
  { key: 'الكيمياء',             ar: 'الكيمياء',       en: 'Chemistry',    emoji: '🧪' },
  { key: 'الأحياء',               ar: 'الأحياء',         en: 'Biology',      emoji: '🧬' },
  { key: 'اللغة الإنجليزية',  ar: 'الإنجليزية',     en: 'English',      emoji: '🅰️' },
  { key: 'اللغة العربية',      ar: 'العربية',         en: 'Arabic',       emoji: '📖' },
  { key: 'الحاسوب',             ar: 'الحاسوب',        en: 'Computing',    emoji: '💻' },
  { key: 'الدراسات الاجتماعية', ar: 'الاجتماعيات', en: 'Social Studies', emoji: '🌍' },
];

const TutorDirectory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { language, toggleLanguage, localize } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const isAr = language === 'ar';

  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSubject, setActiveSubject] = useState(searchParams.get('subject'));
  const [activeMode, setActiveMode] = useState(searchParams.get('mode') || 'any');
  const [minRating, setMinRating] = useState(0);
  // Autism / special-needs specialist filter (Feb 2026 launch)
  const [specialNeedsOnly, setSpecialNeedsOnly] = useState(searchParams.get('specialty') === 'special_needs');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const params = {};
        if (activeSubject) params.subject = activeSubject;
        if (activeMode !== 'any') params.teaching_mode = activeMode;
        if (minRating > 0) params.min_rating = minRating;
        const res = await axios.get(`${API}/teachers/search`, { params });
        setTutors(Array.isArray(res.data) ? res.data : []);
      } catch {
        setTutors([]);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    load();
  }, [activeSubject, activeMode, minRating]);

  /* Local search filter: name + bio + subjects + (optional) specialty */
  const filteredTutors = useMemo(() => {
    let pool = tutors;
    if (specialNeedsOnly) {
      pool = pool.filter((t) =>
        t.specialty === 'special_needs' ||
        t.specialty === 'autism' ||
        t.special_needs_specialist === true
      );
    }
    if (!searchQuery.trim()) return pool;
    const q = searchQuery.toLowerCase().trim();
    return pool.filter((t) => {
      const haystack = [
        t.user_name || t.name || '',
        t.bio || '',
        ...(t.subjects || []),
        ...(t.languages || []),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [tutors, searchQuery, specialNeedsOnly]);

  /* Click "Book" → public users go to register, logged-in students → teacher profile */
  const handleBook = (tutor) => {
    if (user) {
      navigate(`/student/teacher/${tutor.id}`);
    } else {
      // Save the tutor they wanted, then redirect after registration
      sessionStorage.setItem('intended_tutor', tutor.id);
      navigate(`/register?next=/student/teacher/${tutor.id}`);
    }
  };

  const t = (ar, en) => localize({ ar, en });

  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-[#09090B]' : 'bg-[#F8FAFC]'}`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="tutor-directory-page"
    >
      {/* ─── Top nav (lightweight, public) ─── */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-navy-800/95 border-b border-white/5 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="back-to-landing"
          >
            <img src="/lwt-icon-new.png" alt="LWT" className="h-8 w-8 rounded-lg" />
            <span className="font-bold text-sm hidden sm:inline">Learn With Trust</span>
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-slate-300 hover:text-white hover:bg-white/10 h-8 px-2" data-testid="theme-toggle">
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-slate-300 hover:text-white hover:bg-white/10 h-8 px-3 text-xs" data-testid="language-toggle">
              <Globe className="w-3.5 h-3.5 mr-1" />{isAr ? 'English' : 'العربية'}
            </Button>
            <div className="hidden sm:block">
              <SuiteSwitcher language={language} current="lwt" compact />
            </div>
            {user ? (
              <Button onClick={() => navigate('/student/dashboard')} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-9 px-5 text-xs font-bold" data-testid="dashboard-btn">
                {t('لوحة التحكم', 'Dashboard')}
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-200 hover:text-white hover:bg-white/10 text-xs h-9 font-medium hidden sm:inline-flex" data-testid="login-btn">
                  {t('تسجيل الدخول', 'Login')}
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-9 px-5 text-xs font-bold shadow-[0_4px_20px_rgba(79,70,229,0.35)]" data-testid="register-btn">
                  {t('انضم الآن', 'Sign up')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── Hero / Search bar ─── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy-800 via-navy-700 to-indigo-800 text-white">
        <div className="absolute inset-0 opacity-20"
             style={{
               background:
                 'radial-gradient(600px 400px at 20% 20%, rgba(129,140,248,0.4), transparent 60%),' +
                 'radial-gradient(600px 400px at 80% 80%, rgba(245,158,11,0.2), transparent 60%)',
             }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-1.5 bg-amber-400/20 text-amber-200 text-xs font-bold px-4 py-1.5 rounded-full border border-amber-400/30 mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            {t('تصفح بدون تسجيل', 'Browse without sign-up')}
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4" style={{ lineHeight: isAr ? 1.4 : 1.1 }}>
            {t('تصفّح أفضل المدرسين', 'Find your perfect tutor')}
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed">
            {t(
              'استكشف معلمين معتمدين، تخصصات، تقييمات. عند اختيارك المعلم المناسب أنشئ حسابك بدقيقة.',
              'Discover verified tutors, subjects, and ratings. Create an account in 60 seconds when you find your match.',
            )}
          </p>

          {/* Search bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className={`absolute ${isAr ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('ابحث عن معلم، مادة، أو لغة…', 'Search tutors, subjects, languages…')}
                className={`w-full h-14 rounded-2xl bg-white text-slate-900 outline-none text-sm font-medium ${isAr ? 'pr-12 pl-4' : 'pl-12 pr-4'} placeholder:text-slate-400 shadow-2xl focus:ring-4 focus:ring-indigo-400/30 transition-all`}
                data-testid="tutor-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute ${isAr ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400`}
                  aria-label="clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Subject chips */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 max-w-3xl mx-auto">
            <button
              onClick={() => setActiveSubject(null)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${!activeSubject ? 'bg-white text-indigo-700' : 'bg-white/10 hover:bg-white/15 text-slate-200'}`}
              data-testid="subject-filter-all"
            >
              {t('كل المواد', 'All subjects')}
            </button>
            {SUBJECTS.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSubject(activeSubject === s.key ? null : s.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${
                  activeSubject === s.key ? 'bg-white text-indigo-700' : 'bg-white/10 hover:bg-white/15 text-slate-200'
                }`}
                data-testid={`subject-filter-${s.en.toLowerCase().replace(/\s/g,'-')}`}
              >
                <span aria-hidden>{s.emoji}</span>
                {localize({ ar: s.ar, en: s.en })}
              </button>
            ))}
            {/* Autism / Special-Needs filter chip — surfaces tutors who self-declare
                or are admin-tagged as specialists. Hidden from the default flow but
                one click away for the families who need it. */}
            <button
              onClick={() => setSpecialNeedsOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ms-1 ring-1 ${
                specialNeedsOnly
                  ? 'bg-teal-500 text-white ring-teal-400'
                  : isDark
                    ? 'bg-teal-500/15 text-teal-200 ring-teal-400/30 hover:bg-teal-500/25'
                    : 'bg-teal-50 text-teal-700 ring-teal-200 hover:bg-teal-100'
              }`}
              data-testid="filter-special-needs"
              aria-pressed={specialNeedsOnly}
            >
              <span aria-hidden>🤝</span>
              {localize({ ar: 'متخصّصو طيف التوحد', en: 'Autism specialists' })}
            </button>
          </div>
        </div>
      </section>

      {/* ─── Mode + filter bar ─── */}
      <div className={`sticky top-14 z-30 backdrop-blur-md ${isDark ? 'bg-[#09090B]/80 border-b border-white/5' : 'bg-white/80 border-b border-slate-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { key: 'any',              icon: null,       ar: 'الكل',                       en: 'All modes' },
              { key: 'online',           icon: Video,      ar: 'أونلاين',                    en: 'Online' },
              { key: 'student_home',     icon: Home,       ar: 'منزل الطالب',                 en: "Student's Home" },
              { key: 'learning_center',  icon: Building2,  ar: 'مركز معتمد',                  en: 'Learning Center', comingSoon: true },
            ].map((opt) => {
              const Icon = opt.icon;
              const isActive = activeMode === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setActiveMode(opt.key)}
                  className={`relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-[0_4px_14px_rgba(79,70,229,0.3)]'
                      : isDark
                        ? 'bg-white/5 text-slate-300 hover:bg-white/10'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`mode-filter-${opt.key}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {localize({ ar: opt.ar, en: opt.en })}
                  {opt.comingSoon && (
                    <span
                      className="ms-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200"
                      data-testid={`coming-soon-${opt.key}`}
                    >
                      {localize({ ar: 'قريباً', en: 'Soon' })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <span className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {filteredTutors.length} {t('نتيجة', 'results')}
            </span>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${isDark ? 'bg-white/5 text-slate-300 hover:bg-white/10' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-3.5 h-3.5" />
              {t('فلاتر', 'Filters')}
            </button>
          </div>
        </div>

        {/* (Beta/demo banner removed Feb 2026 — site is production-ready) */}

        {/* Expandable filter panel */}
        {showFilters && RATINGS_ENABLED && (
          <div className={`px-4 sm:px-6 pb-3 pt-1 ${isDark ? 'border-t border-white/5' : 'border-t border-slate-100'}`}>
            <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
              <span className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {t('الحد الأدنى للتقييم:', 'Min rating:')}
              </span>
              {[0, 3, 4, 4.5].map((r) => (
                <button
                  key={r}
                  onClick={() => setMinRating(r)}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                    minRating === r
                      ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300'
                      : isDark ? 'bg-white/5 text-slate-300 hover:bg-white/10' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`min-rating-${r}`}
                >
                  {r === 0 ? t('الكل', 'Any') : <><Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {r}+</>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Results ─── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-10">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
            {[...Array(8)].map((_, i) => <SkeletonCard key={i} isDark={isDark} />)}
          </div>
        ) : filteredTutors.length === 0 ? (
          <EmptyState isAr={isAr} isDark={isDark} onClearAll={() => {
            setSearchQuery(''); setActiveSubject(null); setActiveMode('any'); setMinRating(0);
          }} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
            {filteredTutors.map((tutor, i) => (
              <TutorCard
                key={tutor.id || i}
                tutor={tutor}
                isAr={isAr}
                isDark={isDark}
                onBook={() => handleBook(tutor)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ─── Sign-up nudge ─── */}
      {!user && (
        <section className={`py-12 ${isDark ? 'bg-[#0d1117]' : 'bg-white'}`}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              {t('سريع وآمن', 'Quick & secure')}
            </div>
            <h2 className={`text-2xl md:text-3xl font-black mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('وجدت معلمك؟ أنشئ حسابك بدقيقة', 'Found your tutor? Sign up in 60 seconds')}
            </h2>
            <p className={`mb-6 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t('احجز درسك الأول مجاناً واستفد من ضمان الجودة الكامل.', 'Book your first lesson free and unlock our full quality guarantee.')}
            </p>
            <Button onClick={() => navigate('/register')} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-8 text-base font-bold shadow-[0_8px_25px_rgba(79,70,229,0.3)]" data-testid="signup-cta-btn">
              {t('أنشئ حساباً مجانياً', 'Create a free account')}
              <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

/* ── Tutor card ──────────────────────────────────────────── */
const MODE_ICON = {
  online: Video,
  student_home: Home,
  learning_center: Building2,
  // legacy aliases (in case some teachers still use older keys)
  home: Home,
  center: Building2,
};
const MODE_LABEL_AR = {
  online: 'أونلاين',
  student_home: 'منزل الطالب',
  learning_center: 'مركز معتمد',
};
const MODE_LABEL_EN = {
  online: 'Online',
  student_home: "Student's Home",
  learning_center: 'Certified Center',
};

const TutorCard = ({ tutor, isAr, isDark, onBook }) => {
  const { localize } = useLanguage();
  const name = tutor.user_name || tutor.name || tutor.user?.name || (localize({ ar: 'معلّم', en: 'Tutor' }));
  const subjects = (tutor.subjects || []).slice(0, 3);
  const rating = Number(tutor.rating || tutor.average_rating || 0);
  const reviewsCount = tutor.total_reviews || tutor.reviews_count || 0;
  const price = tutor.hourly_rate || tutor.price_per_hour;
  const picture = tutor.profile_picture || tutor.profile_image || tutor.user_picture || tutor.user?.profile_picture;
  const available = tutor.is_available_now || false;
  const lessonTypes = tutor.lesson_types || ['online'];
  // Prefer the granular teaching_modes if present; fallback to legacy lesson_types for older profiles
  const teachingModes = (tutor.teaching_modes && tutor.teaching_modes.length > 0)
    ? tutor.teaching_modes
    : (tutor.lesson_types || ['online']);
  const city = (tutor.emirates && tutor.emirates[0]) || tutor.emirate;
  const verified = tutor.is_verified;
  const initial = name.charAt(0);

  return (
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className={`group rounded-2xl overflow-hidden border transition-all ${
        isDark
          ? 'bg-[#161b22] border-[#30363d] hover:border-indigo-600/50'
          : 'bg-white border-slate-100 hover:border-indigo-200 hover:shadow-[0_16px_40px_rgba(79,70,229,0.12)]'
      } shadow-[0_2px_10px_rgba(15,23,42,0.04)]`}
      data-testid={`tutor-card-${tutor.id}`}
    >
      {/* Picture */}
      <div className={`relative h-44 ${isDark ? 'bg-[#0d1117]' : 'bg-slate-100'} overflow-hidden`}>
        {picture ? (
          <img src={picture} alt={name} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-indigo-100 to-indigo-200">
            <span className="text-7xl font-black text-indigo-400">{initial}</span>
          </div>
        )}

        {available && (
          <div className="absolute top-3 start-3 inline-flex items-center gap-1.5 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow">
            <Circle className="w-2 h-2 fill-white text-white animate-pulse" />
            {localize({ ar: 'متاح الآن', en: 'Available now' })}
          </div>
        )}
        {verified && !available && (
          <div className="absolute top-3 start-3 inline-flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow">
            <Sparkles className="w-2.5 h-2.5" />
            {localize({ ar: 'معتمد', en: 'Verified' })}
          </div>
        )}

        <div className="absolute bottom-3 end-3 flex items-center gap-1" data-testid={`tutor-modes-${tutor.id}`}>
          {teachingModes.map((m) => {
            const Icon = MODE_ICON[m] || Video;
            const label = localize({
              ar: MODE_LABEL_AR[m] || m,
              en: MODE_LABEL_EN[m] || m,
            });
            return (
              <span
                key={m}
                className="w-7 h-7 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-sm"
                title={label}
                aria-label={label}
                data-testid={`tutor-mode-icon-${m}`}
              >
                <Icon className="w-3.5 h-3.5 text-indigo-600" />
              </span>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className={`font-bold text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{name}</h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <TrustScoreBadge tutor={tutor} size="sm" language={isAr ? 'ar' : 'en'} />
            {(tutor.specialty === 'special_needs' || tutor.specialty === 'autism' || tutor.special_needs_specialist) && (
              <SpecialNeedsBadge size="sm" />
            )}
            {RATINGS_ENABLED && rating > 0 && (
              <div className="flex items-center gap-0.5">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span className={`text-xs font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{rating.toFixed(1)}</span>
                {reviewsCount > 0 && <span className="text-[10px] text-slate-400">({reviewsCount})</span>}
              </div>
            )}
          </div>
        </div>

        {/* Subject chips */}
        {subjects.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {subjects.map((s, i) => (
              <span key={i} className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${isDark ? 'bg-indigo-500/10 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                {s}
              </span>
            ))}
          </div>
        )}

        {/* Meta line */}
        <div className={`flex items-center gap-3 text-[11px] mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          {city && (
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{city}</span>
          )}
          {tutor.experience_years > 0 && (
            <span className="inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" />{tutor.experience_years}+ {localize({ ar: 'سنة', en: 'yrs' })}</span>
          )}
          {tutor.languages && tutor.languages.length > 0 && (
            <span className="inline-flex items-center gap-1"><Languages className="w-3 h-3" />{tutor.languages[0]}</span>
          )}
        </div>

        {/* Price + Book button */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-[#30363d]">
          <div>
            {price ? (
              <>
                <span className={`text-lg font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{price}</span>
                <span className={`text-[10px] ms-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{localize({ ar: 'د.إ/ساعة', en: 'AED/hr' })}</span>
              </>
            ) : (
              <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{localize({ ar: 'تواصل', en: 'Contact' })}</span>
            )}
          </div>
          <button
            type="button"
            onClick={onBook}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-colors group-hover:gap-2"
            data-testid={`book-tutor-${tutor.id}`}
          >
            {localize({ ar: 'احجز', en: 'Book' })}
            <ArrowRight className={`w-3 h-3 ${isAr ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

/* ── Skeleton ────────────────────────────────────────────── */
const SkeletonCard = ({ isDark }) => (
  <div className={`rounded-2xl overflow-hidden border animate-pulse ${isDark ? 'bg-[#161b22] border-[#30363d]' : 'bg-white border-slate-100'}`}>
    <div className={`h-44 ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
    <div className="p-4 space-y-3">
      <div className={`h-4 w-3/4 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      <div className="flex gap-1.5">
        <div className={`h-3 w-12 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
        <div className={`h-3 w-12 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      </div>
      <div className={`h-3 w-1/2 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      <div className="flex justify-between pt-3">
        <div className={`h-5 w-16 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
        <div className={`h-7 w-16 rounded-full ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      </div>
    </div>
  </div>
);

/* ── Empty state ─────────────────────────────────────────── */
const EmptyState = ({ isAr, isDark, onClearAll }) => (
  <div className="text-center py-20">
    <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5 ${isDark ? 'bg-[#161b22]' : 'bg-slate-100'}`}>
      <BookOpen className="w-10 h-10 text-slate-400" />
    </div>
    <h3 className={`text-xl font-black mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
      {localize({ ar: 'لا يوجد معلمون بهذه الفلاتر', en: 'No tutors match your filters' })}
    </h3>
    <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
      {localize({ ar: 'جرّبي إزالة بعض الفلاتر للحصول على المزيد من النتائج.', en: 'Try removing some filters to see more results.' })}
    </p>
    <Button onClick={onClearAll} variant="outline" className="rounded-full" data-testid="clear-filters-btn">
      {localize({ ar: 'مسح الفلاتر', en: 'Clear filters' })}
    </Button>
  </div>
);

export default TutorDirectory;
